import { parseISO } from 'date-fns';
import { compact } from 'lodash';
import { PackageSearch } from 'lucide-react';
import { useEffect, useState } from 'react';

import { ShipmentStatusIndicator } from '@/components/modules/shipment/table/status-indicator';
import { useMyShipments } from '@/hooks/use-my-shipments';
import { SAVED_ITEM_TYPE, TRACKING_URL } from '@/lib/constants';
import { SavedItemModel } from '@/lib/models/saved-item/types';
import { ShipmentModel } from '@/lib/models/shipment/types';
import { getShipmentIdTypeLabel } from '@/lib/models/shipment/utils/get-shipment-id-type';
import { getTrackingTypeAndValue } from '@/lib/models/tracking-request/utils/get-tracking-type-and-value';
import { MapMarker } from '@/types';
import { formatDateTimeForUser } from '@/utils/format/date-time';
import { logEvent } from '@/utils/logger';
import { createMarkerFromShipmentModel } from '@/utils/mapping';
import { trackUser } from '@/utils/tracking';

import { DashboardCard } from '../card';
import { DashboardEmpty } from '../empty';
import { DashboardListItem } from '../list-item';

const buildShipmentDescriptionString = (shipment?: ShipmentModel) => {
  try {
    if (shipment) {
      const { ata, eta, originPortCode, destinationPortCode } = shipment;
      const parsedAta = ata ? parseISO(ata) : '';
      const parsedEta = eta ? parseISO(eta) : '';
      const fromTo = `${originPortCode || '-'} to ${destinationPortCode || '-'}`;
      const ataString = parsedAta ? `Arrived: ${formatDateTimeForUser(parsedAta)}` : '';
      const etaString = parsedEta ? `ETA: ${formatDateTimeForUser(parsedEta)}` : '';

      return `${fromTo} • ${ataString || etaString || 'ETA unknown'}`;
    }
  } catch (e: any) {
    logEvent.error('Error parsing shipment description string', { shipment, e });
  }
  return '...';
};

export interface DashboardMyShipmentsProps {
  savedItems: SavedItemModel[];
  onMarkerChange?: (markers: MapMarker[]) => void;
  onCreateSavedItem: (item: SavedItemModel) => void;
  onDeleteSavedItem: (item: SavedItemModel) => void;
}

const DashboardMyShipments = ({
  savedItems = [],
  onDeleteSavedItem,
  onMarkerChange,
  onCreateSavedItem,
}: DashboardMyShipmentsProps) => {
  const [activeShipmentId, setActiveShipmentId] = useState<string | null>(null);

  const filteredItems = savedItems.filter((item) => item.type === SAVED_ITEM_TYPE.SHIPMENT);
  const shipmentIds = compact(filteredItems.map((item) => item.data?.id));

  // Get my shipments
  const { shipments } = useMyShipments({ ids: shipmentIds, includeCreatedByMe: true });
  const hasShipments = shipments.length > 0;

  // Publish markers to the parent map
  useEffect(() => {
    if (shipments.length) {
      const markers = compact(
        shipments.map((shipment) => createMarkerFromShipmentModel(shipment, activeShipmentId === shipment.id)),
      );
      onMarkerChange?.(markers);
    }
  }, [shipments, activeShipmentId]);

  const handleRowClick = (label: string) => {
    trackUser.event(`Clicked my shipment`, { label, type: 'shipment', from: 'Dashboard' });
  };

  const handleStarClick = async (shipment: ShipmentModel, savedItem?: SavedItemModel) => {
    const isCollection = Boolean(shipment?.associatedContainers?.length);
    const { type = '', value = '' } = shipment ? getTrackingTypeAndValue(shipment) : {};

    if (savedItem) {
      await onDeleteSavedItem(savedItem);
    } else {
      if (!type || !value) return;
      const data = isCollection ? { [type]: value } : { id: shipment.id };

      const newSavedItem: SavedItemModel = {
        label: value,
        type: SAVED_ITEM_TYPE.SHIPMENT,
        data,
      };
      await onCreateSavedItem(newSavedItem);
    }
  };

  const handleRowMouseover = (shipmentId: string | null) => {
    setActiveShipmentId(shipmentId);
  };

  return (
    <DashboardCard
      title="My tracked shipments"
      icon={<PackageSearch />}
      ctaHref={hasShipments ? TRACKING_URL : undefined}
      ctaText="See all shipments"
      isSkinless
    >
      {hasShipments && (
        <ul>
          {shipments.map((shipment) => {
            const savedItem = filteredItems.find((i) => i.data?.id === shipment.id);

            const { id, status, subStatus } = shipment;
            const trackingId = getShipmentIdTypeLabel(shipment);
            const href = `${TRACKING_URL}?id=${id}`;

            return (
              <DashboardListItem
                key={id}
                title={savedItem?.label || trackingId.label}
                href={href}
                description={`${buildShipmentDescriptionString(shipment)}`}
                onItemClick={() => handleRowClick(trackingId.label)}
                onStarClick={() => handleStarClick(shipment, savedItem)}
                onItemMouseLeave={() => handleRowMouseover(null)}
                onItemMouseOver={() => handleRowMouseover(id!)}
                isSaved={!!savedItem}
                actionColumn={<ShipmentStatusIndicator status={status} subStatus={subStatus} />}
              />
            );
          })}
        </ul>
      )}
      {!hasShipments && (
        <DashboardEmpty
          title="Shipments you create or watch will appear here"
          cta="Go to shipments"
          href={TRACKING_URL}
        />
      )}
    </DashboardCard>
  );
};

export { DashboardMyShipments };
